import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Paper, Grid, Box } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { formatCurrency, formatMinutes, formatTurkishDate } from '../helpers';

/**
 * Farklı düğüm derinlikleri için stil tanımları
 */
const depthStyles = {
    // Root düğüm (görünmez)
    0: {
        fill: 'white',
        stroke: 'none',
        strokeWidth: 0,
        fillOpacity: 1,
        textColor: 'black',
    },
    // Sipariş (stokHareketi) düğümleri
    1: {
        fill: '#e2725b',
        stroke: '#333',
        strokeWidth: 2,
        fillOpacity: 0.2,
        textColor: 'black',
    },
    // İş emri düğümleri
    2: {
        fill: '#69b3a2',
        stroke: '#555',
        strokeWidth: 0.5,
        fillOpacity: 0.5,
        textColor: '#333',
    },
    // Üretim planı düğümleri
    3: {
        fill: '#9ac1f0',
        stroke: '#666',
        strokeWidth: 0.5,
        fillOpacity: 0.9,
        textColor: 'black',
    },
    // Alt iş emirleri içindeki planlar (depth >= 4)
    4: {
        fill: '#a3c4bc',
        stroke: '#666',
        strokeWidth: 0.5,
        fillOpacity: 0.9,
        textColor: 'black',
    },
};

// Varsayılan stil değerleri
const defaultStyle = {
    fill: '#cccccc',
    stroke: '#999',
    strokeWidth: 0.5,
    fillOpacity: 0.8,
    textColor: 'black',
};

/**
 * Verilen düğüm derinliği için stil alır, eğer tanımlı değilse en derin stili kullanır
 * @param {number} depth - Düğüm derinliği
 * @param {string} prop - İstenen stil özelliği
 * @returns {*} - Stil değeri
 */
function getStyleForDepth(depth, prop) {
    // depth undefined veya null ise varsayılan değere dön
    if (depth === undefined || depth === null) {
        return defaultStyle[prop] || '';
    }

    // Maksimum tanımlı derinlik (4 varsayalım, sayının dinamik hesaplanması daha iyi olabilir)
    const maxDefinedDepth = 4;

    // Derinlik negatif veya çok büyükse, en yakın geçerli değere düşür
    const safeDepth = Math.min(Math.max(0, depth), maxDefinedDepth);

    // Belirtilen derinlik için stil yoksa, en yakın küçük derinliği bul
    let styleDepth = safeDepth;
    while (styleDepth >= 0 && !depthStyles[styleDepth]) {
        styleDepth--;
    }

    // Hala bulunamadıysa varsayılan değer döndür
    if (styleDepth < 0 || !depthStyles[styleDepth]) {
        return defaultStyle[prop] || '';
    }

    // Stil özelliği yoksa varsayılan değere dön
    return depthStyles[styleDepth][prop] !== undefined ? depthStyles[styleDepth][prop] : defaultStyle[prop] || '';
}

/**
 * Verilen sipariş verilerini CirclePacking görselleştirmesi için dönüştürür.
 *
 * @param {Array} data - Sipariş verileri dizisi
 * @returns {Object} CirclePacking ağaç yapısı
 */
function buildHierarchy(data) {
    // Stok hareketlerinin toplam tutarını hesapla (dış balonların oranlaması için)
    const totalSiparisValue = data.reduce((total, item) => {
        const tutar = item?.stokHareketi?.Tutar || item?.Tutar || 0;
        return total + tutar;
    }, 0);

    // Kök düğüm
    const root = {
        name: 'Toplam Sipariş',
        children: [],
    };

    // Her sipariş için
    data.forEach((item) => {
        const stokHareketi = item.stokHareketi || item;
        const stokAdi = stokHareketi?.KartAdi || stokHareketi?.StokAdi || 'Bilinmeyen Stok';
        const siparisNo = stokHareketi?.SiparisNo || '';
        const stokTutar = stokHareketi?.Tutar || 0;

        // Dış katman - Sipariş balonu
        const siparisNode = {
            name: stokAdi,
            siparisNo: siparisNo,
            tutar: stokTutar,
            tutarOran: totalSiparisValue > 0 ? stokTutar / totalSiparisValue : 0,
            value: stokTutar, // Dış balonların hacmini belirlemek için tutar ekle
            children: [],
        };

        // İş emirleri varsa ekle
        if (item.isEmri) {
            // İş emrinin kendisi
            processIsEmriNode(item.isEmri, siparisNode);
        }

        // Eğer alt düğüm yoksa, ama tutar varsa value atanmış olmalı
        if (siparisNode.children.length === 0 && stokTutar > 0) {
            siparisNode.value = stokTutar;
        }

        root.children.push(siparisNode);
    });

    return root;
}

/**
 * İş emri bilgilerini işler ve ağaç yapısına ekler
 *
 * @param {Object} isEmri - İş emri verisi
 * @param {Object} parentNode - Ebeveyn düğüm
 */
function processIsEmriNode(isEmri, parentNode) {
    // İş emri detayları
    const receteAdi = isEmri.isEmri?.[0]?.ReceteAdi || 'Bilinmeyen Reçete';
    const isEmriNo = isEmri.IsEmriNo || isEmri.isEmri?.[0]?.IsEmriNo || '';
    const isEmriID = isEmri.IsEmriID || isEmri.isEmri?.[0]?.IsEmriID || '';
    const cariAdi = isEmri.CariAdi || isEmri.isEmri?.[0]?.CariAdi || '';
    const birimAdi = isEmri.BirimAdi || isEmri.isEmri?.[0]?.BirimAdi || '';
    const miktar = isEmri.Miktar || isEmri.isEmri?.[0]?.Miktar || 0;
    const siparisNo = isEmri.SiparisNo || isEmri.isEmri?.[0]?.SiparisNo || '';
    const tarih = isEmri.Tarih || isEmri.isEmri?.[0]?.Tarih || '';
    const teslimTarihi = isEmri.TeslimTarihi || isEmri.isEmri?.[0]?.TeslimTarihi || '';
    const kapali = isEmri.Kapali || isEmri.isEmri?.[0]?.Kapali || false;
    const aciklama = isEmri.Aciklama || isEmri.isEmri?.[0]?.Aciklama || '';
    // Tutar bilgisini direkt olarak isEmri'den veya isEmri.isEmri'den al
    const tutar =
        isEmri.Tutar !== undefined
            ? isEmri.Tutar
            : isEmri.isEmri && isEmri.isEmri[0] && isEmri.isEmri[0].Tutar !== undefined
            ? isEmri.isEmri[0].Tutar
            : 0;

    // Orta katman - İş emri düğümü
    const isEmriNode = {
        name: receteAdi,
        isEmriNo: isEmriNo,
        isEmriID: isEmriID,
        cariAdi: cariAdi,
        birimAdi: birimAdi,
        miktar: miktar,
        siparisNo: siparisNo,
        tarih: tarih,
        teslimTarihi: teslimTarihi,
        kapali: kapali,
        aciklama: aciklama,
        tutar: tutar,
        value: tutar > 0 ? tutar : 0.1, // İş emri balonlarının hacmini belirlemek için tutar ekle, minimum değer 0.1
        children: [],
    };

    // İç katman - Üretim planları
    if (Array.isArray(isEmri.uretimPlanlari) && isEmri.uretimPlanlari.length > 0) {
        isEmri.uretimPlanlari.forEach((plan) => {
            const operasyonAdi = plan.OperasyonAdi || 'Bilinmeyen Operasyon';
            // Miktar ve süre değerleri
            const miktar = plan.MiktarToplam || plan.Miktar || 0;
            const sureToplam = plan.SureToplam || 0;
            const birim = plan.BirimAdi || 'Ad';

            // Süre değeri negatif veya 0 ise minimum pozitif değer kullan (görünür olması için)
            const safeValue = sureToplam <= 0 ? 0.1 : sureToplam;

            // Üretim planı düğümü
            const planNode = {
                name: operasyonAdi,
                miktar: miktar,
                birim: birim,
                sureToplam: sureToplam,
                sureKalan: plan.SureKalan || 0,
                miktarKalan: plan.MiktarKalan || 0,
                isEmriNo: plan.IsEmriNo || isEmriNo,
                isEmriID: plan.IsEmriID || isEmriID,
                makineAdi: plan.MakineAdi || '',
                makineKodu: plan.MakineKodu || '',
                operasyonKodu: plan.OperasyonKodu || '',
                operasyonSirasi: plan.OperasyonSirasi || 0,
                tarih: plan.Tarih || '',
                kapali: plan.Kapali || false,
                value: safeValue, // İç halkaların büyüklüğünü SureToplam değeri belirler
            };

            isEmriNode.children.push(planNode);
        });
    }

    // Alt iş emirleri varsa, onları da işle
    if (Array.isArray(isEmri.altIsEmirleri) && isEmri.altIsEmirleri.length > 0) {
        isEmri.altIsEmirleri.forEach((altIsEmri) => {
            processIsEmriNode(altIsEmri, isEmriNode);
        });
    }

    // Hiç çocuğu yoksa ama tutar varsa, value atanmış olmalı
    if (isEmriNode.children.length === 0 && tutar > 0) {
        isEmriNode.value = tutar;
    }

    parentNode.children.push(isEmriNode);
}

/**
 * Tooltip içeriğini hazırlar
 * @param {Object} d - Düğüm verisi
 * @returns {string} HTML içeriği
 */
function buildTooltipContent(d) {
    if (!d || d.depth === 0) return ''; // Root veya geçersiz düğüm için tooltip yok

    let content = '';

    if (d.depth === 1) {
        // Stok hareketi (Sipariş)
        content = `<strong>${d.data.name || 'Bilinmiyor'}</strong><br>
                 ${d.data.siparisNo ? `Sipariş No: ${d.data.siparisNo}<br>` : ''}
                 Tutar: ${(d.data.tutar || 0).toLocaleString('tr-TR')} TL<br>
                 Toplam İçindeki Pay: %${((d.data.tutarOran || 0) * 100).toFixed(2)}`;
    } else if (d.depth === 2) {
        // İş emri
        content = `<strong>${d.data.name || 'Bilinmiyor'}</strong><br>
                 ${d.data.isEmriNo ? `İş Emri No: ${d.data.isEmriNo}<br>` : ''}
                 Tutar: ${(d.data.tutar || 0).toLocaleString('tr-TR')} TL`;
    } else if (d.depth >= 3) {
        // Üretim planı veya daha derin düğümler
        const tarih = d.data.tarih ? formatTurkishDate(d.data.tarih, true) : '';
        content = `<strong>${d.data.name || 'Bilinmiyor'}</strong><br>
                 ${d.data.makineAdi ? `Makine: ${d.data.makineAdi}<br>` : ''}
                 Toplam Süre: ${formatMinutes(d.data.sureToplam || 0)}<br>
                 Miktar: ${d.data.miktar || 0} ${d.data.birim || 'Ad'}
                 ${tarih ? `<br>Tarih: ${tarih}` : ''}`;
    }

    return content;
}

/**
 * Metnin daireye sığıp sığmayacağını kontrol eder ve gerekirse kısaltır
 * @param {string} text - Gösterilecek metin
 * @param {number} radius - Daire yarıçapı
 * @param {number} fontSize - Font boyutu (px)
 * @returns {string} - Kısaltılmış veya orijinal metin
 */
function fitTextToCircle(text, radius, fontSize = 10) {
    if (!text) return '';

    // Kabaca font genişliği (her karakter için ortalama genişlik)
    const avgCharWidth = fontSize * 0.6;

    // Daire içine kaç karakter sığar?
    // 2*r * 0.8 = çapın %80'i kadar uzunluk (biraz marj bırakalım)
    const maxChars = Math.floor((2 * radius * 0.8) / avgCharWidth);

    if (text.length <= maxChars) {
        return text;
    }

    // Uzunsa kısalt
    if (maxChars <= 6) return ''; // Çok küçükse hiç gösterme
    if (maxChars <= 10) return text.substring(0, maxChars - 2) + '..';

    // Baştan ve sondan parçalar göster
    const firstPart = text.substring(0, Math.floor(maxChars / 2) - 2);
    const lastPart = text.substring(text.length - Math.floor(maxChars / 2) + 2);
    return `${firstPart}...${lastPart}`;
}

/**
 * Düğüm detaylarını dialog içinde göstermek için bileşen
 */
function NodeDetailsDialog({ open, onClose, nodeData }) {
    if (!nodeData) return null;

    // Dialog başlığını ve içeriğini düğüm tipine göre belirle
    let title = '';
    let content = null;

    // İş emri detay linkini oluştur
    const getIsEmriLink = (isEmriID) => {
        if (!isEmriID) return null;
        // Tam URL oluştur (http/https ile)
        return `https://erp.aaro.com.tr/UrIsEmri/Ozet?id=${isEmriID}`;
    };

    // Düğüm derinliğine göre içerik oluştur
    if (nodeData.depth === 1) {
        // Stok hareketi (Sipariş)
        title = nodeData.data.name || 'Stok Detayı';
        content = (
            <MDBox>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <MDTypography variant="subtitle1" fontWeight="medium">
                                Sipariş Bilgileri
                            </MDTypography>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Sipariş No:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.siparisNo || '-'}</MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Tutar:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {(nodeData.data.tutar || 0).toLocaleString('tr-TR')} TL
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Toplam İçindeki Pay:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        %{((nodeData.data.tutarOran || 0) * 100).toFixed(2)}
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </MDBox>
        );
    } else if (nodeData.depth === 2) {
        // İş emri
        title = nodeData.data.name || 'İş Emri Detayı';

        // İş emri ID'si ve link
        const isEmriID = nodeData.data.isEmriID || '';
        const isEmriLink = getIsEmriLink(isEmriID);

        content = (
            <MDBox>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <MDTypography variant="subtitle1" fontWeight="medium">
                                İş Emri Bilgileri
                            </MDTypography>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        İş Emri No:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.isEmriNo || '-'}</MDTypography>
                                </Grid>

                                {isEmriID && (
                                    <>
                                        <Grid size={6}>
                                            <MDTypography variant="body2" color="text.secondary">
                                                İş Emri ID:
                                            </MDTypography>
                                        </Grid>
                                        <Grid size={6}>
                                            {isEmriLink ? (
                                                <MDTypography
                                                    variant="body2"
                                                    component="a"
                                                    href={isEmriLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    color="info"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {isEmriID} (Görüntüle)
                                                </MDTypography>
                                            ) : (
                                                <MDTypography variant="body2">{isEmriID}</MDTypography>
                                            )}
                                        </Grid>
                                    </>
                                )}

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Müşteri:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.cariAdi || '-'}</MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Birim:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.birimAdi || '-'}</MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Miktar:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {(nodeData.data.miktar || 0).toLocaleString('tr-TR')}{' '}
                                        {nodeData.data.birimAdi || 'Ad'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Reçete Tutar:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {(nodeData.data.tutar || 0).toLocaleString('tr-TR')} TL
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Sipariş No:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.siparisNo || '-'}</MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Tarih:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {formatTurkishDate(nodeData.data.tarih) || '-'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Teslim Tarihi:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {formatTurkishDate(nodeData.data.teslimTarihi) || '-'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Durum:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography
                                        variant="body2"
                                        color={nodeData.data.kapali ? 'success.main' : 'warning.main'}
                                        fontWeight="medium"
                                    >
                                        {nodeData.data.kapali ? 'Tamamlandı' : 'Devam Ediyor'}
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {nodeData.data.aciklama && (
                        <Grid size={12}>
                            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                                <MDTypography variant="subtitle1" fontWeight="medium">
                                    Açıklama
                                </MDTypography>
                                <MDTypography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                                    {nodeData.data.aciklama}
                                </MDTypography>
                            </Paper>
                        </Grid>
                    )}

                    {nodeData.children && nodeData.children.length > 0 && (
                        <Grid size={12}>
                            <Paper elevation={1} sx={{ p: 2 }}>
                                <MDTypography variant="subtitle1" fontWeight="medium">
                                    Üretim Planları
                                </MDTypography>
                                <Box component="ul" sx={{ pl: 2 }}>
                                    {nodeData.children.map((child, idx) => (
                                        <Box component="li" key={idx} sx={{ mb: 1 }}>
                                            <MDTypography variant="body2">
                                                {child.data.name || `Plan ${idx + 1}`} -
                                                {child.data.makineAdi ? ` ${child.data.makineAdi}` : ''} -
                                                {formatTurkishDate(child.data.tarih, true)}
                                            </MDTypography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </MDBox>
        );
    } else if (nodeData.depth >= 3) {
        // Üretim planı veya daha derin düğümler
        title = `${nodeData.data.name || 'Üretim Planı'} ${
            nodeData.data.makineAdi ? `(${nodeData.data.makineAdi})` : ''
        }`;

        const tarihFormatted = formatTurkishDate(nodeData.data.tarih);

        // İş emri ID'si ve link
        const isEmriID = nodeData.data.isEmriID || '';
        const isEmriLink = getIsEmriLink(isEmriID);

        content = (
            <MDBox>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <MDTypography variant="subtitle1" fontWeight="medium">
                                Üretim Bilgileri
                            </MDTypography>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Operasyon:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {nodeData.data.name} ({nodeData.data.operasyonKodu || 'Kod yok'})
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Makine:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {nodeData.data.makineAdi || '-'}{' '}
                                        {nodeData.data.makineKodu ? `(${nodeData.data.makineKodu})` : ''}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        İş Emri No:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.isEmriNo || '-'}</MDTypography>
                                </Grid>

                                {isEmriID && (
                                    <>
                                        <Grid size={6}>
                                            <MDTypography variant="body2" color="text.secondary">
                                                İş Emri ID:
                                            </MDTypography>
                                        </Grid>
                                        <Grid size={6}>
                                            {isEmriLink ? (
                                                <MDTypography
                                                    variant="body2"
                                                    component="a"
                                                    href={isEmriLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    color="info"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {isEmriID} (Görüntüle)
                                                </MDTypography>
                                            ) : (
                                                <MDTypography variant="body2">{isEmriID}</MDTypography>
                                            )}
                                        </Grid>
                                    </>
                                )}

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Toplam Miktar:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {(nodeData.data.miktar || 0).toLocaleString('tr-TR')}{' '}
                                        {nodeData.data.birim || 'Ad'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Kalan Miktar:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {(nodeData.data.miktarKalan || 0).toLocaleString('tr-TR')}{' '}
                                        {nodeData.data.birim || 'Ad'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Toplam Süre:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {formatMinutes(nodeData.data.sureToplam || 0)}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Kalan Süre:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">
                                        {formatMinutes(nodeData.data.sureKalan || 0)}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Planlanan Tarih:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{tarihFormatted || '-'}</MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Durum:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography
                                        variant="body2"
                                        color={nodeData.data.kapali ? 'success.main' : 'warning.main'}
                                        fontWeight="medium"
                                    >
                                        {nodeData.data.kapali ? 'Tamamlandı' : 'Devam Ediyor'}
                                    </MDTypography>
                                </Grid>

                                <Grid size={6}>
                                    <MDTypography variant="body2" color="text.secondary">
                                        Operasyon Sırası:
                                    </MDTypography>
                                </Grid>
                                <Grid size={6}>
                                    <MDTypography variant="body2">{nodeData.data.operasyonSirasi || '-'}</MDTypography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </MDBox>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <MDTypography variant="h6">{title}</MDTypography>
            </DialogTitle>
            <DialogContent dividers>{content}</DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Kapat
                </Button>
            </DialogActions>
        </Dialog>
    );
}

NodeDetailsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    nodeData: PropTypes.object,
};

/**
 * CirclePacking görselleştirme bileşeni
 */
function CirclePacking({ data, height = '100%' }) {
    const containerRef = useRef(null);
    const tooltipRef = useRef(null); // Tooltip için ref ekle
    const [selectedNode, setSelectedNode] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleNodeClick = (nodeData) => {
        if (nodeData.depth > 0) {
            // Root node'a tıklamayı işleme alma
            setSelectedNode(nodeData);
            setDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Tooltip göster/gizle fonksiyonu
    const showTooltip = (event, content) => {
        if (!tooltipRef.current) return;

        const tooltip = tooltipRef.current;
        tooltip.innerHTML = content;
        tooltip.style.display = 'block';
        tooltip.style.left = `${event.pageX + 15}px`;
        tooltip.style.top = `${event.pageY + 15}px`;
    };

    // Tooltip gizle fonksiyonu
    const hideTooltip = () => {
        if (!tooltipRef.current) return;
        tooltipRef.current.style.display = 'none';
    };

    useEffect(() => {
        if (!data || data.length === 0) return;

        // Önceki SVG'yi temizle
        d3.select(containerRef.current).selectAll('*').remove();

        try {
            // Veriyi CirclePacking formatına dönüştür
            const hierarchyData = buildHierarchy(data);

            // Görünüm boyutları
            const width = 960;
            const height = 960;

            // D3 hiyerarşisini oluştur
            const root = d3
                .hierarchy(hierarchyData)
                .sum((d) => d.value || 0)
                .sort((a, b) => (b.value || 0) - (a.value || 0));

            // Maksimum derinliği bul (renk skalası için)
            const maxDepth = Math.max(d3.max(root.descendants(), (d) => d.depth) || 4, 4);

            // CirclePacking layout
            const pack = d3.pack().size([width, height]).padding(3);

            // Layout uygula
            const packedData = pack(root);

            // SVG oluştur
            const svg = d3
                .select(containerRef.current)
                .append('svg')
                .attr('viewBox', [0, 0, width, height])
                .attr('width', '100%')
                .attr('height', '100%')
                .attr('preserveAspectRatio', 'xMidYMid meet')
                .attr('style', 'max-width: 100%; height: 100%; font: 10px sans-serif;')
                .attr('text-anchor', 'middle');

            // Renk skalası - Katmanlara göre (dinamik)
            const colorScale = d3
                .scaleOrdinal()
                .domain(d3.range(0, maxDepth + 1)) // 0 -> maxDepth arası domainimiz
                .range(
                    d3.range(0, maxDepth + 1).map((d) => {
                        const style = getStyleForDepth(d, 'fill');
                        return style || defaultStyle.fill;
                    })
                );

            // Tüm nodeler için grup oluştur
            const node = svg
                .append('g')
                .attr('text-anchor', 'middle')
                .selectAll('g')
                .data(packedData.descendants())
                .join('g')
                .attr('transform', (d) => `translate(${d.x},${d.y})`);

            // Nodların çember gösterimi
            node.append('circle')
                .attr('r', (d) => d.r)
                .attr('fill', (d) => colorScale(d.depth))
                .attr('fill-opacity', (d) => getStyleForDepth(d.depth, 'fillOpacity'))
                .attr('stroke', (d) => getStyleForDepth(d.depth, 'stroke'))
                .attr('stroke-width', (d) => getStyleForDepth(d.depth, 'strokeWidth'))
                .attr('cursor', 'pointer')
                .on('mouseover', function (event, d) {
                    // Fare üzerindeyken
                    event.stopPropagation();

                    // Hover efekti
                    d3.select(this)
                        .attr('stroke', '#000')
                        .attr('stroke-width', Math.min(2, d.r * 0.1));

                    // Tooltip içeriği oluştur
                    const content = buildTooltipContent(d);

                    // İçerik varsa tooltip'i göster
                    if (content && content.trim() !== '') {
                        showTooltip(event, content);
                    }
                })
                .on('mousemove', function (event) {
                    // Fare hareket ederken tooltip'i takip et
                    if (tooltipRef.current && tooltipRef.current.style.display !== 'none') {
                        tooltipRef.current.style.left = `${event.pageX + 15}px`;
                        tooltipRef.current.style.top = `${event.pageY + 15}px`;
                    }
                })
                .on('mouseout', function () {
                    // Fare çıkınca
                    // Node'un derinliğini alıp stil uygula
                    const nodeData = d3.select(this.parentNode).datum();
                    const depth = nodeData ? nodeData.depth : 0;

                    // Hover efektini kaldır
                    d3.select(this)
                        .attr('stroke', getStyleForDepth(depth, 'stroke'))
                        .attr('stroke-width', getStyleForDepth(depth, 'strokeWidth'));

                    // Tooltip'i gizle
                    hideTooltip();
                })
                .on('click', function (event, d) {
                    // Düğüme tıklandığında
                    event.stopPropagation();
                    handleNodeClick(d);
                });

            // İş emirleri için etiketler (orta katman)
            node.filter((d) => d.depth === 2 && d.r > 20)
                .append('text')
                .attr('clip-path', (d) => `circle(${d.r * 0.8})`)
                .selectAll('tspan')
                .data((d) => {
                    // Daireye sığacak şekilde ismi kısalt
                    const shortName = fitTextToCircle(d.data.name, d.r, 10);
                    return shortName ? [shortName] : [];
                })
                .join('tspan')
                .attr('x', 0)
                .attr('y', (d, i, nodes) => `${i - nodes.length / 2 + 0.8}em`)
                .attr('fill', (d) => getStyleForDepth(2, 'textColor'))
                .attr('font-size', (d) => '10px')
                .text((d) => d);

            // Üretim planları için etiketler (iç katman)
            node.filter((d) => d.depth >= 3 && d.r > 16)
                .append('text')
                .attr('clip-path', (d) => `circle(${d.r * 0.8})`)
                .selectAll('tspan')
                .data((d) => {
                    // Operasyon adını daire boyutuna göre kısalt
                    const operasyonAdi = fitTextToCircle(d.data.name, d.r, 8);

                    // Süre bilgisi
                    let sureText = '';
                    if (d.r > 24) {
                        // Sadece yeterince büyük dairelerde süre göster
                        sureText = formatMinutes(d.data.sureToplam || 0);
                    }

                    return operasyonAdi ? (sureText ? [operasyonAdi, sureText] : [operasyonAdi]) : [];
                })
                .join('tspan')
                .attr('x', 0)
                .attr('y', (d, i, nodes) => `${i - nodes.length / 2 + 0.8}em`)
                .attr('fill', (d) => getStyleForDepth(3, 'textColor'))
                .attr('font-size', (d) => '8px')
                .text((d) => d);
        } catch (error) {
            console.error('CirclePacking hata:', error, error.stack);
            // Hata durumunda basit bir mesaj göster
            d3.select(containerRef.current)
                .append('div')
                .style('color', 'red')
                .style('padding', '20px')
                .style('text-align', 'center')
                .text('Görselleştirme oluşturulurken bir hata oluştu: ' + error.message);
        }

        return () => {
            // Component unmount olduğunda temizlik
            d3.select(containerRef.current).selectAll('*').remove();
        };
    }, [data]);

    return (
        <>
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: height,
                    position: 'relative',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    padding: '10px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            />

            {/* Özel tooltip div */}
            <div
                ref={tooltipRef}
                style={{
                    position: 'absolute',
                    display: 'none',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px',
                    maxWidth: '240px',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '12px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    pointerEvents: 'none',
                    zIndex: 10000,
                    opacity: 0.95,
                }}
            />

            {/* Düğüm detayları için dialog */}
            <NodeDetailsDialog open={dialogOpen} onClose={handleCloseDialog} nodeData={selectedNode} />
        </>
    );
}

CirclePacking.propTypes = {
    data: PropTypes.array.isRequired,
    height: PropTypes.string,
};

export default CirclePacking;
